import { DOCUMENT } from '@angular/common';
import {
  ErrorHandler,
  inject,
  Service,
} from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk [\w-]+ failed|ChunkLoadError|error loading dynamically imported module/i;
const GLOBAL_RECOVERY_KEY = 'atlas-app:chunk-recovery:global';
const NAVIGATION_RECOVERY_PREFIX = 'atlas-app:chunk-recovery:navigation:';

/**
 * Centralizes stale JavaScript chunk recovery for both router and global errors.
 *
 * A full-document navigation is necessary because browsers cache failed dynamic
 * imports for the current document. Each target gets one guarded retry to avoid
 * a broken deployment causing an infinite reload loop.
 */
@Service()
export class ChunkRecoveryService {
  private readonly defaultErrorHandler = new ErrorHandler();
  private readonly document = inject(DOCUMENT);
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly memoryRecoveryKeys = new Set<string>();
  private recoveryInProgress = false;

  /** Starts recovery for a failed lazy-route navigation and preserves its URL. */
  recoverNavigation(error: unknown, targetUrl: string): boolean {
    if (!this.isChunkLoadError(error)) {
      return false;
    }

    this.beginRecovery(this.navigationRecoveryKey(targetUrl), targetUrl);
    return true;
  }

  /** Queues recovery for a non-router chunk error, allowing router recovery to win races. */
  scheduleGlobalRecovery(error: unknown): boolean {
    if (!this.isChunkLoadError(error)) {
      return false;
    }

    Promise.resolve().then(() => {
      if (!this.recoveryInProgress) {
        this.beginRecovery(GLOBAL_RECOVERY_KEY);
      }
    });
    return true;
  }

  /** Restores a successful route's retry budget for a later, separate deployment. */
  clearNavigationRecovery(targetUrl: string): void {
    this.clearRecoveryKey(this.navigationRecoveryKey(targetUrl));
  }

  private beginRecovery(recoveryKey: string, targetUrl?: string): void {
    if (this.recoveryInProgress) {
      return;
    }

    if (!this.claimRecovery(recoveryKey)) {
      this.defaultErrorHandler.handleError(
        new Error('Chunk recovery failed again after a reload; not retrying to avoid a loop.'),
      );
      return;
    }

    this.recoveryInProgress = true;
    void this.activateAndNavigate(targetUrl);
  }

  private async activateAndNavigate(targetUrl?: string): Promise<void> {
    try {
      await this.swUpdate?.activateUpdate();
    } catch (error) {
      this.defaultErrorHandler.handleError(
        new Error('Chunk recovery could not activate the ready service-worker version.', {
          cause: error,
        }),
      );
    }

    const location = this.document.defaultView?.location;
    if (!location) {
      return;
    }

    if (targetUrl) {
      location.assign(targetUrl);
      return;
    }

    location.reload();
  }

  private isChunkLoadError(error: unknown): boolean {
    return CHUNK_ERROR_PATTERN.test(this.extractErrorMessage(error));
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return `${error.name} ${error.message}`;
    }

    if (typeof error === 'string') {
      return error;
    }

    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  private navigationRecoveryKey(targetUrl: string): string {
    return NAVIGATION_RECOVERY_PREFIX + targetUrl;
  }

  private claimRecovery(key: string): boolean {
    const storage = this.getSessionStorage();
    if (!storage) {
      if (this.memoryRecoveryKeys.has(key)) {
        return false;
      }

      this.memoryRecoveryKeys.add(key);
      return true;
    }

    if (storage.getItem(key)) {
      return false;
    }

    storage.setItem(key, '1');
    return true;
  }

  private clearRecoveryKey(key: string): void {
    const storage = this.getSessionStorage();
    if (storage) {
      storage.removeItem(key);
      return;
    }

    this.memoryRecoveryKeys.delete(key);
  }

  private getSessionStorage(): Storage | undefined {
    try {
      return this.document.defaultView?.sessionStorage;
    } catch {
      return undefined;
    }
  }
}
